import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../lib/store";
import { DataTable } from "./DataTable";
import { EntityForm } from "./EntityForm";
import { useOpenById } from "../lib/useOpenById";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Plus } from "lucide-react";

export function ListPage({ module, titleKey, columns, onRowClick, extraToolbar, transformRows, filters, listQuery, formProps, formSubtitle }) {
  const { t, canWrite } = useApp();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    const sep = listQuery ? `&${listQuery}` : "";
    api.get(`/${module}?limit=500${sep}`).then((r) => setRows(transformRows ? transformRows(r.data) : r.data)).catch(() => {});
  }, [module, transformRows, listQuery]);

  useEffect(load, [load]);
  useEffect(() => {
    if (params.get("new") === "1") { setEditing({}); setParams({}, { replace: true }); }
  }, [params, setParams]);
  useOpenById(module, (doc) => (onRowClick ? onRowClick(doc) : setEditing(doc)));

  return (
    <div className="space-y-4" data-testid={`${module}-page`}>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t(titleKey)}</h1>
        {canWrite && (
          <Button data-testid={`${module}-new-btn`} onClick={() => setEditing({})}
            className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
            <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("new")}
          </Button>
        )}
      </div>
      <DataTable module={module} rows={rows} columns={columns} onImported={load} filters={filters}
        onRowClick={onRowClick || ((r) => setEditing(r))} toolbar={extraToolbar?.({ rows, reload: load })} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? t("edit") : t("create")} – {t(titleKey)}</DialogTitle>
            {formSubtitle && !editing?.id && <DialogDescription>{formSubtitle}</DialogDescription>}</DialogHeader>
          {editing && (
            <EntityForm module={module} initial={editing} {...(formProps || {})}
              onSaved={() => { setEditing(null); load(); }}
              onDeleted={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
